import { InfoIcon } from "../Icons";
import { SkeletonLoader } from "../SkeletonLoader";
import { TooltipWrapper } from "../Tooltip";
import { Typo } from "../Typo";

type StatCardProps = {
  title: string;
  value: string;
  unit?: string;
  skeleton?: boolean;
  isError?: boolean;
  info?: string;
  /**
   * Exact-figure tooltip on the value itself (distinct from `info`, which
   * tooltips the title). Set only when `value` is a shortened ("mln"/"bln")
   * amount — a full amount has no tooltip.
   */
  valueTooltip?: string;
};

export const StatCard = ({
  title,
  value,
  unit,
  skeleton,
  isError,
  info,
  valueTooltip,
}: StatCardProps) => (
  <div
    className="flex min-h-[123px] flex-col gap-4 rounded-2xl bg-surface-brand-subtle p-6 font-sans"
    // Lets a test assert on one card's state: several cards can show the same
    // value or dash at once, so text queries alone can't isolate them.
    data-testid={`stat-card-${title}`}
  >
    <div className="flex items-center gap-1">
      <Typo element="span" type="labelSM" textColor="text-text-secondary">
        {title}
      </Typo>
      {info && (
        // `color="blue"` and the `InfoIcon` glyph match every other tooltip
        // trigger on this report (SubIBStatCards, SubIBsTable header cells, both
        // detail drawers). This card was the only one left on TooltipWrapper's
        // white default and on `BaseInfoIcon`, whose extra `stroke-opacity="0.6"`
        // rendered a fainter, differently-shaped glyph than its neighbours.
        <TooltipWrapper
          cursor="help"
          id={`stat-info-${title}`}
          text={info}
          color="blue"
          tooltipMaxWidth={240}
          tooltipPlacement="top"
          tooltipContainerClassName="!text-xs"
        >
          <InfoIcon className="h-3.5 w-3.5 shrink-0 [&_path]:stroke-icon-tertiary" />
        </TooltipWrapper>
      )}
    </div>
    <div className="flex items-baseline gap-2">
      {skeleton ? (
        <SkeletonLoader width={96} height={40} variant="rounded" />
      ) : isError ? (
        <Typo element="span" type="heading2XL" textColor="text-text-disabled">
          --
        </Typo>
      ) : (
        <>
          {valueTooltip ? (
            <TooltipWrapper
              text={valueTooltip}
              color="blue"
              openEvents={{ click: true }}
              // The stats strip scrolls horizontally inside its own
              // `overflow-x-auto` wrapper (see SharedRebateProgramsView.tsx /
              // MobileKpiRail.tsx) — without "fixed", react-tooltip's
              // absolute-positioned panel gets clipped/mispositioned by that
              // ancestor, same reason MoneyValue/renderMoneyField use it.
              positionStrategy="fixed"
              tooltipPlacement="top"
              tooltipMaxWidth={240}
              tooltipContainerClassName="!text-xs"
            >
              <span className="flex items-baseline gap-2">
                <Typo
                  element="span"
                  type="heading2XL"
                  textColor="text-text-primary"
                >
                  {value}
                </Typo>
                {unit && (
                  <Typo
                    element="span"
                    type="labelSM"
                    textColor="text-text-secondary"
                  >
                    {unit}
                  </Typo>
                )}
              </span>
            </TooltipWrapper>
          ) : (
            <>
              <Typo
                element="span"
                type="heading2XL"
                textColor="text-text-primary"
              >
                {value}
              </Typo>
              {unit && (
                <Typo
                  element="span"
                  type="labelSM"
                  textColor="text-text-secondary"
                >
                  {unit}
                </Typo>
              )}
            </>
          )}
        </>
      )}
    </div>
  </div>
);
